import { createAsyncThunk } from '@reduxjs/toolkit';
import focusBearApi from 'services/axios-config';
import { toast } from 'react-toastify';
import { captureException } from 'services/posthog.service';
import { t } from 'services/i18n.service';
import Endpoints from 'constants/endpoints';
import { HTTP_STATS_CODE } from 'constants/general';
import { CreateMcpTokenPayload, McpToken, McpTokenIssued } from 'interfaces/mcp-token.interface';
import { McpAgent } from 'interfaces/mcp-agent.interface';
import {
  updateIsCreatingToken,
  updateIsFetchingTokens,
  updateIsMcpAgentsLoading,
  updateIsRevokingTokenId
} from './slice';

export const getMcpTokens = createAsyncThunk(
  'mcp_token/get_tokens',
  async (_, { dispatch }) => {
    try {
      dispatch(updateIsFetchingTokens(true));
      const { data } = await focusBearApi.get<McpToken[]>(Endpoints.MCP_TOKENS);
      return data ?? [];
    } catch (error) {
      captureException(error);
      return [];
    } finally {
      dispatch(updateIsFetchingTokens(false));
    }
  }
);

export const createMcpToken = createAsyncThunk(
  'mcp_token/create_token',
  async (payload: CreateMcpTokenPayload, { dispatch }) => {
    try {
      dispatch(updateIsCreatingToken(true));
      const { data, status } = await focusBearApi.post<McpTokenIssued>(
        Endpoints.MCP_TOKENS,
        payload
      );
      if (status === HTTP_STATS_CODE.CREATED) {
        toast.success(t('mcp_tokens.token_created_successfully'));
        dispatch(getMcpTokens());
        return data;
      }
      return null;
    } catch (error) {
      toast.error(t('mcp_tokens.could_not_create_token'));
      captureException(error);
      return null;
    } finally {
      dispatch(updateIsCreatingToken(false));
    }
  }
);

export const getMcpAgents = createAsyncThunk(
  'mcp_token/get_agents',
  async (_, { dispatch }) => {
    try {
      dispatch(updateIsMcpAgentsLoading(true));
      const { data } = await focusBearApi.get<McpAgent[]>(Endpoints.MCP_AGENTS);
      return data ?? [];
    } catch (error) {
      captureException(error);
      return [];
    } finally {
      dispatch(updateIsMcpAgentsLoading(false));
    }
  }
);

export const revokeMcpToken = createAsyncThunk(
  'mcp_token/revoke_token',
  async (id: string, { dispatch }) => {
    try {
      dispatch(updateIsRevokingTokenId(id));
      const { status } = await focusBearApi.delete(Endpoints.MCP_TOKEN_BY_ID(id));
      if (status === HTTP_STATS_CODE.NO_CONTENT) {
        toast.success(t('mcp_tokens.token_revoked_successfully'));
        dispatch(getMcpTokens());
        return id;
      }
      return null;
    } catch (error) {
      toast.error(t('mcp_tokens.could_not_revoke_token'));
      captureException(error);
      return null;
    } finally {
      dispatch(updateIsRevokingTokenId(null));
    }
  }
);
